import { asDefined, assert, ByteArrayInput, ByteArrayOutput, Checksum, isDefined, Listeners, Option, panic, Predicates, UUID } from "@opendaw/lib-std";
import { Address } from "./address";
import { DeleteUpdate, NewUpdate, PointerUpdate, PrimitiveUpdate } from "./updates";
import { Dispatchers } from "./dispatchers";
import { GraphEdges } from "./graph-edges";
export class BoxGraph {
    #boxFactory;
    #boxes;
    #deferredPointerUpdates;
    #updateListeners;
    #immediateUpdateListeners;
    #transactionListeners;
    #dispatchers;
    #edges;
    #pointerTransactionState;
    #finalizeTransactionObservers;
    #deletionListeners;
    #inTransaction = false;
    #constructingBox = false;
    constructor(boxFactory = Option.None) {
        this.#boxFactory = boxFactory;
        this.#boxes = UUID.newSet(box => box.address.uuid);
        this.#deferredPointerUpdates = [];
        this.#dispatchers = Dispatchers.create();
        this.#updateListeners = new Listeners();
        this.#immediateUpdateListeners = new Listeners();
        this.#transactionListeners = new Listeners();
        this.#edges = new GraphEdges();
        this.#pointerTransactionState = Address.newSet(({ pointer }) => pointer.address);
        this.#finalizeTransactionObservers = [];
        this.#deletionListeners = UUID.newSet(entry => entry.uuid);
    }
    beginTransaction() {
        assert(!this.#inTransaction, "Transaction already in progress");
        this.#inTransaction = true;
        this.#transactionListeners.proxy.onBeginTransaction();
    }
    endTransaction() {
        assert(this.#inTransaction, "No transaction in progress");
        if (this.#deferredPointerUpdates.length > 0) {
            this.#deferredPointerUpdates.forEach(({ pointerField, update }) => this.#processPointerVertexUpdate(pointerField, update));
            this.#deferredPointerUpdates.length = 0;
        }
        this.#pointerTransactionState.values()
            .toSorted((a, b) => a.index - b.index)
            .forEach(({ pointer, initial, final }) => {
            if (!initial.equals(final)) {
                initial.ifSome(address => this.findVertex(address).unwrapOrUndefined()?.pointerHub.onRemoved(pointer));
                final.ifSome(address => this.findVertex(address).unwrapOrUndefined()?.pointerHub.onAdded(pointer));
            }
        });
        this.#pointerTransactionState.clear();
        this.#inTransaction = false;
        // it is possible that new observers will be added while executing
        while (this.#finalizeTransactionObservers.length > 0) {
            this.#finalizeTransactionObservers.splice(0).forEach(observer => observer());
            if (this.#finalizeTransactionObservers.length > 0) {
                console.debug(`${this.#finalizeTransactionObservers.length} new observers while notifying`);
            }
        }
        this.#transactionListeners.proxy.onEndTransaction();
    }
    inTransaction() { return this.#inTransaction; }
    constructingBox() { return this.#constructingBox; }
    createBox(name, uuid, constructor) {
        return this.#boxFactory.unwrap("No box-factory installed")(name, this, uuid, constructor);
    }
    stageBox(box, constructor) {
        this.#assertTransaction();
        assert(!this.#constructingBox, "Cannot construct box while other box is constructing");
        if (isDefined(constructor)) {
            this.#constructingBox = true;
            constructor(box);
            this.#constructingBox = false;
        }
        const added = this.#boxes.add(box);
        assert(added, () => `${box.name} ${box.address.toString()} already staged`);
        const update = new NewUpdate(box.address.uuid, box.name, box.toArrayBuffer());
        this.#updateListeners.proxy.onUpdate(update);
        this.#immediateUpdateListeners.proxy.onUpdate(update);
        return box;
    }
    subscribeTransaction(listener) {
        return this.#transactionListeners.subscribe(listener);
    }
    subscribeToAllUpdates(listener) {
        return this.#updateListeners.subscribe(listener);
    }
    subscribeToAllUpdatesImmediate(listener) {
        return this.#immediateUpdateListeners.subscribe(listener);
    }
    subscribeVertexUpdates(propagation, address, procedure) {
        return this.#dispatchers.subscribe(propagation, address, procedure);
    }
    subscribeEndTransaction(observer) { this.#finalizeTransactionObservers.push(observer); }
    subscribeDeletion(uuid, listener) {
        const entry = this.#deletionListeners.getOrCreate(uuid, () => ({ uuid, listeners: new Set() }));
        entry.listeners.add(listener);
        return {
            terminate: () => {
                entry.listeners.delete(listener);
                if (entry.listeners.size === 0) {
                    this.#deletionListeners.removeByKeyIfExist(uuid);
                }
            }
        };
    }
    unstageBox(box) {
        this.#assertTransaction();
        const deleted = this.#boxes.removeByKey(box.address.uuid);
        assert(deleted === box, `${box} could not be found to unstage`);
        this.#edges.unwatchVerticesOf(box);
        const update = new DeleteUpdate(box.address.uuid, box.name, box.toArrayBuffer());
        this.#deletionListeners.removeByKeyIfExist(box.address.uuid)?.listeners.forEach(listener => listener());
        this.#updateListeners.proxy.onUpdate(update);
        this.#immediateUpdateListeners.proxy.onUpdate(update);
    }
    findBox(uuid) {
        return this.#boxes.opt(uuid);
    }
    findVertex(address) {
        return this.#boxes.opt(address.uuid).flatMap(box => box.searchVertex(address.fieldKeys));
    }
    boxes() { return this.#boxes.values(); }
    edges() { return this.#edges; }
    checksum() {
        const checksum = new Checksum();
        this.boxes().forEach(box => box.write(checksum));
        return checksum.result();
    }
    onPrimitiveValueUpdate(field, oldValue, newValue) {
        this.#assertTransaction();
        if (field.isAttached() && !this.#constructingBox) {
            const update = new PrimitiveUpdate(field.address, field.serialization(), oldValue, newValue);
            this.#dispatchers.dispatch(update);
            this.#updateListeners.proxy.onUpdate(update);
            this.#immediateUpdateListeners.proxy.onUpdate(update);
        }
    }
    onPointerAddressUpdated(pointerField, oldValue, newValue) {
        this.#assertTransaction();
        if (oldValue.nonEmpty()) {
            this.#edges.disconnect(pointerField);
        }
        if (newValue.nonEmpty()) {
            this.#edges.connect(pointerField, newValue.unwrap());
        }
        const update = new PointerUpdate(pointerField.address, oldValue, newValue);
        if (this.#constructingBox) {
            this.#deferredPointerUpdates.push({ pointerField, update });
        }
        else {
            this.#processPointerVertexUpdate(pointerField, update);
            this.#immediateUpdateListeners.proxy.onUpdate(update);
        }
    }
    #processPointerVertexUpdate(pointerField, update) {
        const { oldAddress, newAddress } = update;
        pointerField.resolvedTo(newAddress.flatMap(address => this.findVertex(address)));
        const optState = this.#pointerTransactionState.opt(pointerField.address);
        optState.match({
            none: () => this.#pointerTransactionState.add({
                pointer: pointerField,
                initial: oldAddress,
                final: newAddress,
                index: this.#pointerTransactionState.size()
            }),
            some: state => state.final = newAddress
        });
        this.#dispatchers.dispatch(update);
        this.#updateListeners.proxy.onUpdate(update);
    }
    findOrphans(rootBox) {
        const connectedBoxes = this.#collectAllConnectedBoxes(rootBox);
        return this.boxes().filter(box => !connectedBoxes.has(box));
    }
    #collectAllConnectedBoxes(rootBox) {
        const visited = new Set();
        const queue = [rootBox];
        while (queue.length > 0) {
            const box = queue.pop();
            if (visited.has(box)) {
                continue;
            }
            visited.add(box);
            this.#collectPointersFromBox(box).forEach(pointer => {
                pointer.targetAddress.ifSome(address => {
                    this.findBox(address.uuid).ifSome(targetBox => {
                        if (!visited.has(targetBox)) {
                            queue.push(targetBox);
                        }
                    });
                });
            });
            for (const pointer of box.incomingEdges()) {
                if (!visited.has(pointer.box)) {
                    queue.push(pointer.box);
                }
            }
        }
        return visited;
    }
    #collectPointersFromBox(box) {
        const pointers = [];
        const collectFromFields = (fields) => {
            for (const field of fields) {
                field.accept({
                    visitPointerField: (p) => pointers.push(p),
                    visitObjectField: (o) => collectFromFields(o.fields()),
                    visitArrayField: (a) => collectFromFields(a.fields())
                });
            }
        };
        collectFromFields(box.fields());
        return pointers;
    }
    dependenciesOf(root, options = {}) {
        const excludeBox = isDefined(options.excludeBox) ? options.excludeBox : Predicates.alwaysFalse;
        const alwaysFollowMandatory = isDefined(options.alwaysFollowMandatory) ? options.alwaysFollowMandatory : false;
        const stopAtResources = isDefined(options.stopAtResources) ? options.stopAtResources : false;
        const boxes = new Set();
        const pointers = new Set();
        const trace = (box, isStartingBox = false) => {
            if (boxes.has(box) || (!isStartingBox && excludeBox(box))) {
                return;
            }
            boxes.add(box);
            if (stopAtResources && isDefined(box.resource)) {
                box.incomingEdges()
                    .forEach(pointer => {
                    pointers.add(pointer);
                    const targetsField = pointer.targetAddress.mapOr(address => !address.isBox(), false);
                    if (pointer.mandatory && targetsField) {
                        if (box.resource === "shared") {
                            const isOwnershipField = pointer.targetAddress
                                .flatMap(address => this.findVertex(address))
                                .mapOr(vertex => vertex.pointerRules.mandatory, false);
                            if (isOwnershipField) {
                                return;
                            }
                        }
                        trace(pointer.box);
                    }
                });
                return;
            }
            box.outgoingEdges()
                .filter(([pointer]) => !pointers.has(pointer))
                .forEach(([source, targetAddress]) => {
                const targetVertex = this.findVertex(targetAddress)
                    .unwrap(`Could not find target of ${source.toString()}`);
                pointers.add(source);
                if (targetVertex.pointerRules.mandatory &&
                    (alwaysFollowMandatory || targetVertex.pointerHub.incoming()
                        .filter(p => p.targetAddress.mapOr(address => address.equals(targetAddress), false))
                        .every(pointer => pointers.has(pointer)))) {
                    return trace(targetVertex.box);
                }
            });
            box.incomingEdges()
                .forEach(pointer => {
                pointers.add(pointer);
                if (pointer.mandatory) {
                    trace(pointer.box);
                }
            });
        };
        const startingBoxes = Array.isArray(root) ? root : [root];
        startingBoxes.forEach(startingBox => trace(startingBox, true));
        startingBoxes.forEach(startingBox => boxes.delete(startingBox));
        return { boxes, pointers: Array.from(pointers).reverse() };
    }
    verifyPointers() {
        this.#edges.validateRequirements();
        let count = 0 | 0;
        const verify = (vertex) => {
            for (const field of vertex.fields()) {
                field.accept({
                    visitPointerField: (pointer) => {
                        if (pointer.targetAddress.nonEmpty()) {
                            const isResolved = pointer.targetVertex.nonEmpty();
                            const inGraph = this.findVertex(pointer.targetAddress.unwrap()).nonEmpty();
                            assert(isResolved, `pointer ${pointer.address} is broken`);
                            assert(inGraph, `Cannot find target for pointer ${pointer.address}`);
                            count++;
                        }
                    },
                    visitObjectField: (object) => verify(object)
                });
            }
        };
        this.#boxes.forEach((box) => verify(box));
        console.debug("verification complete.");
        return { count };
    }
    debugBoxes() {
        console.table(this.#boxes.values().reduce((dict, box) => {
            dict[UUID.toString(box.address.uuid)] = {
                class: box.name,
                "incoming links": box.incomingEdges().length,
                "outgoing links": box.outgoingEdges().length,
                "est. memory (bytes)": box.estimateMemory()
            };
            return dict;
        }, {}));
    }
    debugDependencies() {
        console.debug("Dependencies:");
        this.boxes().forEach(box => {
            console.debug(`\t${box}`);
            for (const dependency of this.dependenciesOf(box).boxes) {
                console.debug(`\t\t${dependency}`);
            }
        });
    }
    addressToDebugPath(address) {
        return address.flatMap(address => address.isBox()
            ? this.findBox(address.uuid).map(box => box.name)
            : this.findBox(address.uuid)
                .flatMap(box => box.searchVertex(address.fieldKeys)
                .map(vertex => vertex.isField() ? vertex.debugPath : panic("Unknown address"))));
    }
    toArrayBuffer() {
        const output = ByteArrayOutput.create();
        const boxes = this.#boxes.values();
        output.writeInt(boxes.length);
        boxes.forEach(box => {
            const buffer = box.serialize();
            output.writeInt(buffer.byteLength);
            output.writeBytes(new Int8Array(buffer));
        });
        return output.toArrayBuffer();
    }
    fromArrayBuffer(arrayBuffer) {
        assert(this.#boxes.isEmpty(), "Cannot call fromArrayBuffer if boxes is not empty");
        const input = new ByteArrayInput(arrayBuffer);
        const numBoxes = input.readInt();
        this.beginTransaction();
        const boxes = [];
        for (let i = 0; i < numBoxes; i++) {
            const length = input.readInt();
            const int8Array = new Int8Array(length);
            input.readBytes(int8Array);
            const boxStream = new ByteArrayInput(int8Array.buffer);
            const creationIndex = boxStream.readInt();
            const name = boxStream.readString();
            const uuid = UUID.fromDataInput(boxStream);
            boxes.push({ creationIndex, name, uuid, boxStream });
        }
        boxes
            .sort((a, b) => a.creationIndex - b.creationIndex)
            .forEach(({ name, uuid, boxStream }) => this.createBox(name, uuid, box => box.read(boxStream)));
        this.endTransaction();
    }
    toJSON() {
        const result = {};
        for (const box of this.#boxes.values()) {
            result[box.address.toString()] = {
                name: box.name,
                fields: asDefined(box.toJSON())
            };
        }
        return result;
    }
    fromJSON(value) {
        if (typeof value !== "object" || value === null || Array.isArray(value)) {
            return panic("Expected object");
        }
        this.beginTransaction();
        const entries = Object.entries(value);
        for (const [uuid, { name, fields }] of entries) {
            this.createBox(name, UUID.parse(uuid), box => {
                try {
                    box.fromJSON(fields);
                }
                catch (reason) {
                    console.warn(reason);
                }
            });
        }
        this.endTransaction();
    }
    #assertTransaction() {
        assert(this.#inTransaction, () => "Modification only prohibited in transaction mode.");
    }
}
